// =============================================================================
// NEWSLETTER SECTION COMPONENT
// =============================================================================

import React, { useState } from 'react';
import { Mail, CheckCircle, AlertCircle, Send } from 'lucide-react';
import { Button } from '@/components/Button';
import type { ContentComponent } from '@/types';

interface NewsletterSectionProps {
  component: ContentComponent;
}

/**
 * Newsletter Section Component
 */
export const NewsletterSection: React.FC<NewsletterSectionProps> = ({ component }) => {
  return <NewsletterSectionImpl component={component} />;
};

/**
 * Newsletter Section Implementation
 */
const NewsletterSectionImpl: React.FC<{ component: ContentComponent }> = ({ component }) => {
  const { id, title, content, metadata } = component;
  
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState('');
  
  const backgroundImage = metadata?.backgroundImage;
  const textAlign = metadata?.textAlign || 'center';
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !email.includes('@')) {
      setStatus('error');
      setMessage('Please enter a valid email address.');
      return;
    }
    
    setStatus('loading');
    
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      setStatus('success');
      setMessage('Thank you for subscribing! Check your email for confirmation.');
      setEmail('');
    } catch (error) {
      setStatus('error');
      setMessage('Something went wrong. Please try again.');
    }
  };
  
  const sectionStyle = backgroundImage ? {
    backgroundImage: `url(${backgroundImage})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    position: 'relative' as const,
  } : {};
  
  return (
    <section
      id={id}
      className="content-section newsletter-section"
      style={sectionStyle}
    >
      {backgroundImage && (
        <div className="newsletter-overlay" />
      )}
      
      <div className="container">
        <div
          className="newsletter-content"
          style={{ textAlign, position: 'relative', zIndex: 1 }}
        >
          {title && <h2>{title}</h2>}
          {content && <p>{content}</p>}
          
          <div className="newsletter-form-container">
            <form onSubmit={handleSubmit} className="newsletter-form">
              <div className="form-group">
                <div className="input-wrapper">
                  <Mail size={20} className="input-icon" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email address"
                    className={`newsletter-input ${status === 'error' ? 'error' : ''}`}
                    disabled={status === 'loading'}
                    required
                  />
                </div>
              </div>
              
              <Button
                type="submit"
                variant="primary"
                size="lg"
                disabled={status === 'loading'}
                className="newsletter-submit"
              >
                {status === 'loading' ? (
                  <>
                    <div className="spinner" />
                    Subscribing...
                  </>
                ) : (
                  <>
                    <Send size={20} />
                    Subscribe
                  </>
                )}
              </Button>
            </form>
            
            {status !== 'idle' && (
              <div className={`newsletter-status ${status}`}>
                <div className="status-icon">
                  {status === 'success' ? (
                    <CheckCircle size={20} />
                  ) : (
                    <AlertCircle size={20} />
                  )}
                </div>
                <p className="status-message">{message}</p>
              </div>
            )}
            
            <p className="newsletter-disclaimer">
              No spam, unsubscribe at any time. We respect your privacy.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export { NewsletterSectionImpl };

